import { useQueryClient, useMutation } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  fetchAllResults,
  makeOptionallyAuthenticatedQuery,
  getSavedSearchClient,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import {
  FindAllSortByEnum,
  FindAllSortOrderEnum,
} from '@b-stock/saved-search-api-client'
import type {
  CreateSavedSearchDto,
  PaginatedSavedSearchDto,
  SavedSearchesApiFindAllRequest,
  SavedSearchesApiFindByIdRequest,
  SavedSearchesApiUpdateByIdRequest,
  SavedSearchDto,
  SavedSearchResponseDto,
} from '@b-stock/saved-search-api-client'

type Return = {
  savedSearch: SavedSearchDto
  status: number
  statusText: string
}

const createSavedSearch = async (
  accessToken: string | null,
  createSavedSearchDto: CreateSavedSearchDto
): Promise<Return> => {
  const {
    data: savedSearch,
    status,
    statusText,
  } = await getSavedSearchClient().createSavedSearch(
    { createSavedSearchDto },
    buildAxiosRequestConfig({ accessToken })
  )

  return { savedSearch, status, statusText }
}

const findAll = async (
  accessToken: string | null,
  requestParams: SavedSearchesApiFindAllRequest
): Promise<PaginatedSavedSearchDto> => {
  const axiosParams = buildAxiosRequestConfig({ accessToken })

  const { data } = await getSavedSearchClient().findAll(
    requestParams,
    axiosParams
  )
  return data
}

const PAGE_LIMIT = 100

// Accumulates every page until `total` is covered, matching accounts-portal's
// do/while pagination — findAll on its own only returns the first 100.
const findAllPaginated = async (
  accessToken: string | null,
  requestParams: SavedSearchesApiFindAllRequest
): Promise<PaginatedSavedSearchDto> => {
  const savedSearches = await fetchAllResults({
    apiClient: getSavedSearchClient(),
    query: getSavedSearchClient().findAll,
    // Default to newest-first. An explicit sort still wins
    params: {
      sortBy: [FindAllSortByEnum.CreatedAt],
      sortOrder: [FindAllSortOrderEnum.Desc],
      ...requestParams,
    },
    axiosOptions: buildAxiosRequestConfig({ accessToken }),
    dataKey: 'savedSearches',
    limit: PAGE_LIMIT,
  })

  return {
    savedSearches,
    total: savedSearches.length,
    offset: 0,
    limit: PAGE_LIMIT,
  }
}

const findById = async (
  accessToken: string | null,
  requestParams: SavedSearchesApiFindByIdRequest
): Promise<SavedSearchResponseDto> => {
  const axiosParams = buildAxiosRequestConfig({ accessToken })
  const { data } = await getSavedSearchClient().findById(
    requestParams,
    axiosParams
  )
  return data
}

const updateById = async (
  accessToken: string | null,
  params: SavedSearchesApiUpdateByIdRequest
): Promise<{
  savedSearch: SavedSearchDto
  status: number
  statusText: string
}> => {
  const {
    data: savedSearch,
    status,
    statusText,
  } = await getSavedSearchClient().updateById(
    params,
    buildAxiosRequestConfig({ accessToken })
  )

  return { savedSearch, status, statusText }
}

const useCreateSavedSearch = () => {
  const accessToken = useAccessToken()
  return useMutation({
    mutationFn: (params: CreateSavedSearchDto) =>
      createSavedSearch(accessToken || '', params),
  })
}

const findAllQuery = makeOptionallyAuthenticatedQuery<
  [SavedSearchesApiFindAllRequest],
  PaginatedSavedSearchDto
>('findAll', findAll)

const findAllPaginatedQuery = makeOptionallyAuthenticatedQuery<
  [SavedSearchesApiFindAllRequest],
  PaginatedSavedSearchDto
>('findAllPaginated', findAllPaginated)

const findByIdQuery = makeOptionallyAuthenticatedQuery<
  [SavedSearchesApiFindByIdRequest],
  SavedSearchResponseDto
>('findById', findById)

const useUpdateById = ({
  invalidateOnSuccess = false,
}: { invalidateOnSuccess?: boolean } = {}) => {
  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  return useMutation({
    mutationFn: (params: SavedSearchesApiUpdateByIdRequest) =>
      updateById(accessToken || '', params),
    onSuccess: invalidateOnSuccess
      ? () => {
          void queryClient.invalidateQueries({ queryKey: ['findAll'] })
        }
      : undefined,
  })
}

// Optimistically patches the toggled saved search in every cached
// findAllPaginated result so the switch flips instantly, then reconciles
// with the server on settle (success or error). Only warms findAllPaginated's
// cache namespace — sibling consumers keyed on findAllQuery (home widgets,
// recent-search list, saved-search context, header dropdown) are not
// invalidated. Fine today since none of them display emailsEnabled, but a
// future consumer of this hook that also needs findAllQuery fresh should
// invalidate that key too.
const useUpdateByIdOptimistic = () => {
  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  const queryKey = [findAllPaginatedQuery.name]

  return useMutation({
    mutationFn: (params: SavedSearchesApiUpdateByIdRequest) =>
      updateById(accessToken || '', params),
    onMutate: async (params: SavedSearchesApiUpdateByIdRequest) => {
      await queryClient.cancelQueries({ queryKey })
      const previousData = queryClient.getQueriesData<PaginatedSavedSearchDto>({
        queryKey,
      })

      const isDeactivating = params.updateSavedSearchDto.isActive === false

      queryClient.setQueriesData<PaginatedSavedSearchDto>(
        { queryKey },
        (old) => {
          if (!old) return old
          if (isDeactivating) {
            const savedSearches = old.savedSearches.filter(
              (savedSearch) => savedSearch._id !== params.id
            )
            return { ...old, savedSearches, total: savedSearches.length }
          }
          return {
            ...old,
            savedSearches: old.savedSearches.map((savedSearch) =>
              savedSearch._id === params.id
                ? { ...savedSearch, ...params.updateSavedSearchDto }
                : savedSearch
            ),
          }
        }
      )

      return { previousData }
    },
    onError: (_err, _params, context) => {
      context?.previousData.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      // Mark the list stale without refetching: a delete would otherwise
      // regenerate the Saved Searches carousel's per-search useQueries fan-out
      // and flash skeletons. Trade-off: concurrent changes from other surfaces
      // reconcile on next mount rather than immediately.
      void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
    },
  })
}

export {
  useCreateSavedSearch,
  findAllQuery,
  findAllPaginatedQuery,
  findByIdQuery,
  useUpdateById,
  useUpdateByIdOptimistic,
}
